import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./handheld.css";
import { readPicked, useForgetIfUnusable, writePicked } from "../../../../lib/ingestList.js";

// The picker's Handheld filter (src/lib/ingestPicker.js jobsForScreen), on the job document.
const notForHandheld = (job) => Boolean(job?.guest) || Boolean(job?.studioWalkins && typeof job.studioWalkins === "object");
import IngestJobPicker from "../IngestJobPicker.jsx";
import { useFirestoreCollection, useFirestoreDoc } from "../../../../lib/useFirestore.js";
import { handheld } from "../../../../lib/handheldCallables.js";
import { pad3, projectFileName, attemptExportVersionPath } from "../../../../lib/handheldNaming.js";
import { uploadReferencePhoto, resolvePhotoUrl } from "../../../../lib/handheldStorage.js";
import { usePresets } from "../../../../components/PresetPicker.jsx";
import { AddSubjectForm, CategorySelect, SubjectPresets, nameTaken } from "./subjectForm.jsx";
import BringRawIn from "./BringRawIn.jsx";
import IngestProject from "./IngestProject.jsx";
import IngestExport from "./IngestExport.jsx";
import { useCrewClaims, handheldAccessFor, VIEW_ONLY_MESSAGE, ERASE_ADMIN_ONLY } from "../../../../lib/handheldAccess.js";

/**
 * Handheld / equipment ingest — the SHARED CORE (build plan Segment 1).
 * Ported from the locked mockup (components/handheldIngest/HandheldJobHome.jsx)
 * to match the approved design. Wired to the real Firebase backend.
 *
 * Segment 5 (Ivin, 2026-10-07): whoever runs the capture session ingests.
 * Rig operators and admins see every control; erasing (the ✕ on an empty
 * subject, Combine, a failed screen's clean-up) is an admin's, because it
 * uses up the number; everyone else sees the job view only. The sign-in
 * token's claims decide here, as they do in the callables (handheldAccess.js).
 */
export default function HandheldHome({ onExit }) {
  // Ingest's one picker (IngestJobPicker.jsx), Handheld's list: the same rule
  // as Rigs, without guest jobs and the studio walk-ins jobs (rig only; the
  // server refuses handheld subjects on them too). The job each person picked
  // is remembered in this browser, as on Rigs.
  const [picked] = useState(() => readPicked("handheld"));
  const [jobId, setJobId] = useState(() => (picked?.open ? picked.jobId : null));
  const [selected, setSelected] = useState(() => picked?.jobId ?? null);
  const [restored, setRestored] = useState(() => Boolean(picked?.open));
  const { claims, loading: claimsLoading } = useCrewClaims();
  const open = (id) => {
    setRestored(false);
    setSelected(id);
    setJobId(id);
    writePicked("handheld", { jobId: id, open: true });
  };
  const back = () => {
    if (jobId) writePicked("handheld", { jobId, open: false });
    setRestored(false);
    setJobId(null);
  };
  const forget = useCallback(() => {
    writePicked("handheld", null);
    setRestored(false);
    setSelected(null);
    setJobId(null);
  }, []);
  return (
    <div className="hh-root">
      {jobId ? (
        <JobHome jobId={jobId} access={handheldAccessFor(claims, jobId)} accessLoading={claimsLoading} onBack={back}
          onMissing={restored ? forget : null} />
      ) : (
        <IngestJobPicker screen="handheld" onPick={open} onExit={onExit} selectedId={selected} />
      )}
    </div>
  );
}

// ── A job's subjects ───────────────────────────────────────────────────────────
function JobHome({ jobId, access, accessLoading = false, onBack, onMissing = null }) {
  const { canWrite = false, canErase = false } = access ?? {};
  // The deliverable presets, loaded once for the screen (only someone who may pick one needs them).
  const { presets, error: presetsError } = usePresets({ enabled: canWrite });
  const { data: job, loading: jobLoading, error: jobError } = useFirestoreDoc(`jobs/${jobId}`);
  // A job reopened from memory that is gone, in the trash, or no longer a
  // Handheld job (a guest job, a studio walk-ins job): back to the picker, on
  // the server's word only (never a failed or offline read).
  useForgetIfUnusable({ path: `jobs/${jobId}`, data: job, loading: jobLoading, error: jobError, onMissing, unusable: notForHandheld });
  const { data: subjects, loading } = useFirestoreCollection(`jobs/${jobId}/subjects`);
  const { data: projects } = useFirestoreCollection(`jobs/${jobId}/projects`);
  const [adding, setAdding] = useState(false);
  const [door, setDoor] = useState(null); // 'raw' | 'project' | 'export' | null (Seg 2)

  // Human job label (walwal_260312) for display + derived names; doc id stays the key.
  // The stem the server names folders and requests with (readJobNameStem: the
  // jobId field, else the doc id), so the screen and the bridge agree (Segment 4).
  const jobLabel = (typeof job?.jobId === "string" && job.jobId.trim()) || jobId;
  const projectsById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const sorted = useMemo(() => [...subjects].sort((a, b) => (a.number ?? 0) - (b.number ?? 0)), [subjects]);

  return (
    <div className="hh-core">
      <button type="button" className="ingest-back" onClick={onBack}>← Jobs</button>

      <div className="hh-head">
        <span className="hh-job-pill">{job?.clientName || job?.displayName || jobLabel}</span>
        <span className="mono hh-jobid">{jobLabel}</span>
      </div>
      {presetsError && <div className="hh-name-err" role="alert">Deliverable presets: {presetsError}</div>}

      {canWrite && door === "raw" ? (
        <BringRawIn presets={presets} jobId={jobId} jobLabel={jobLabel} canErase={canErase} onDone={() => setDoor(null)} />
      ) : canWrite && door === "project" ? (
        <IngestProject jobId={jobId} jobLabel={jobLabel} canErase={canErase} onDone={() => setDoor(null)} />
      ) : canWrite && door === "export" ? (
        <IngestExport jobId={jobId} jobLabel={jobLabel} canErase={canErase} onDone={() => setDoor(null)} />
      ) : (
        <>
          <div className="hh-toolbar">
            <div className="hh-toolbar-title">
              Subjects <span className="hh-count">{subjects.length}</span>
              {canWrite && <button type="button" className="hh-addsubj" onClick={() => setAdding((a) => !a)}>+ Add subject</button>}
            </div>
            {canWrite && (
              <div className="hh-doors">
                <button type="button" className="hh-door" onClick={() => setDoor("raw")}>Ingest raw</button>
                <button type="button" className="hh-door" onClick={() => setDoor("project")}>Ingest project</button>
                <button type="button" className="hh-door" onClick={() => setDoor("export")}>Ingest export</button>
              </div>
            )}
          </div>

          {!canWrite && !accessLoading && <div className="hh-access-note" role="note">{VIEW_ONLY_MESSAGE}</div>}
          {canWrite && !canErase && sorted.some(isEmptySubject) && (
            <div className="hh-access-note" role="note">{ERASE_ADMIN_ONLY}</div>
          )}

          {canWrite && adding && (
            <AddSubjectForm presets={presets} jobId={jobId} subjects={subjects} onDone={() => setAdding(false)} onCancel={() => setAdding(false)} />
          )}

          <div className="hh-subject-list">
            {loading ? (
              <div className="hh-empty">Loading subjects…</div>
            ) : sorted.length === 0 ? (
              <div className="hh-empty">No subjects yet. Add the first one.</div>
            ) : (
              sorted.map((s) => (
                <SubjectCard presets={presets} key={s.id} jobId={jobId} jobLabel={jobLabel} subject={s} subjects={subjects} projects={projects} projectsById={projectsById} canWrite={canWrite} canErase={canErase} />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Empty = made, but no data yet (no raw, no project, no export).
const isEmptySubject = (s) => !(s.rawSets?.length) && !(s.projectIds?.length) && !(s.exports?.length);

// ── One subject card (rename / category / erase — Segment 2; who may — Segment 5) ──
function SubjectCard({ presets, jobId, jobLabel, subject, subjects, projects, projectsById, canWrite = false, canErase = false }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(subject.displayName);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const cancelBlurRef = useRef(false);
  const committingRef = useRef(false);

  const hasRaw = (subject.rawSets?.length ?? 0) > 0;
  const projNames = (subject.projectIds ?? [])
    .map((id) => projectFileName(jobLabel ?? jobId, projectsById[id], subjects, projects))
    .filter(Boolean);
  const isEmpty = isEmptySubject(subject);

  const startEdit = () => { if (!canWrite) return; setValue(subject.displayName); setErr(null); setEditing(true); };
  const onChangeName = (v) => {
    setValue(v);
    setErr(nameTaken(subjects, v, subject.id) ? "Name already used on this job" : null);
  };
  const commit = async () => {
    if (cancelBlurRef.current) { cancelBlurRef.current = false; return; }
    if (committingRef.current) return;
    const name = value.trim();
    if (!name) { setErr("Name cannot be blank"); return; }
    if (nameTaken(subjects, name, subject.id)) { setErr("Name already used on this job"); return; }
    if (name === subject.displayName) { setEditing(false); return; }
    committingRef.current = true;
    setBusy(true);
    try {
      await handheld.renameSubject({ jobId, subjectId: subject.id, displayName: name });
      setEditing(false);
    } catch (e) {
      setErr(e?.message ?? "Could not rename");
    } finally {
      setBusy(false);
      setTimeout(() => { committingRef.current = false; }, 0);
    }
  };
  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commit(); }
    if (e.key === "Escape") {
      e.preventDefault();
      cancelBlurRef.current = true;
      setValue(subject.displayName);
      setEditing(false);
      setErr(null);
    }
  };
  const changeCategory = (category) => {
    setErr(null);
    handheld.setCategory({ jobId, subjectId: subject.id, category })
      .catch((e) => setErr(e?.message ?? "Could not change category"));
  };
  const erase = () => {
    if (busy || !canErase) return;
    setBusy(true);
    handheld.deleteSubject({ jobId, subjectId: subject.id })
      .catch((e) => { setErr(e?.message ?? "Could not erase"); setBusy(false); });
  };

  return (
    <div className={`hh-subject ${isEmpty ? "empty" : ""}`}>
      <Thumb jobId={jobId} subject={subject} canWrite={canWrite} />
      <div className="hh-sub-main">
        <div className="hh-sub-top">
          <span className="hh-ppt-num mono">{pad3(subject.number)}</span>
          {editing && canWrite ? (
            <span className="hh-name-edit">
              <input
                className={`hh-name-input ${err ? "bad" : ""}`}
                value={value}
                autoFocus
                disabled={busy}
                onChange={(e) => onChangeName(e.target.value)}
                onKeyDown={onKey}
                onBlur={commit}
              />
              {err && <span className="hh-name-err">{err}</span>}
            </span>
          ) : canWrite ? (
            <button type="button" className="hh-name" onClick={startEdit} title="Rename">{subject.displayName}</button>
          ) : (
            <span className="hh-name hh-name-static">{subject.displayName}</span>
          )}
          {isEmpty && <span className="hh-empty-tag">Empty</span>}
          <CategorySelect value={subject.category} onChange={changeCategory} disabled={!canWrite} />
          <SubjectPresets jobId={jobId} subject={subject} presets={presets} canWrite={canWrite} />
          {isEmpty && canErase && (
            <button type="button" className="hh-erase" title="Erase empty subject" aria-label="Erase empty subject" disabled={busy} onClick={erase}>✕</button>
          )}
          {isEmpty && canWrite && !canErase && (
            <button type="button" className="hh-erase" title={ERASE_ADMIN_ONLY} aria-label={ERASE_ADMIN_ONLY} disabled>✕</button>
          )}
        </div>
        {err && !editing && <span className="hh-name-err">{err}</span>}

        {(subject.capturedBy?.length ?? 0) > 0 && (
          <div className="hh-codes">
            {subject.capturedBy.map((c) => <span key={c} className="hh-code mono">{c}</span>)}
          </div>
        )}

        <div className="hh-stages">
          <div className="hh-stage">
            <span className="hh-stage-label">Raw</span>
            <span className="hh-stage-val">
              {hasRaw
                ? subject.rawSets.map((r, i) => <span key={i} className="mono hh-file">{r.folder}</span>)
                : (subject.projectIds?.length
                  ? <span className="hh-muted">raw in project</span>
                  : <span className="hh-muted">—</span>)}
            </span>
          </div>
          <div className="hh-stage">
            <span className="hh-stage-label">Project</span>
            <span className="hh-stage-val">
              {projNames.length
                ? projNames.map((n) => <span key={n} className="mono hh-file">{n}</span>)
                : <span className="hh-muted">—</span>}
            </span>
          </div>
          <div className="hh-stage">
            <span className="hh-stage-label">Exports</span>
            <span className="hh-stage-val">
              {(subject.exports?.length ?? 0)
                ? subject.exports.map((ex) => {
                    const versions = ex.versions?.length ? ex.versions : [{ label: "v1", current: true }];
                    const ordered = [...versions].sort((a, b) => (b.current ? 1 : 0) - (a.current ? 1 : 0));
                    return ordered.map((v) => {
                      const path = attemptExportVersionPath(jobLabel ?? jobId, ex.code, subject.number, ex.suffix, ex.attempt ?? 1, subject.attempts, v, versions.length) ??
                        "(waiting for this attempt's serial number)";
                      return (
                        <span key={`${ex.id}_${v.label}`} className={`mono hh-file ${ex.lineage ? "fused" : ""}`}>
                          {path}
                          {versions.length > 1 && (
                            <span className={`hh-ver ${v.current ? "current" : ""}`}>{v.label}</span>
                          )}
                        </span>
                      );
                    });
                  })
                : <span className="hh-muted">— not yet</span>}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Reference photo: upload (file picker) + display. The image lives in Storage
// under the subject; the subject record only holds its PATH, so it travels with
// the subject (and into the Captures view at Segment 3).
function Thumb({ jobId, subject, canWrite = false }) {
  const path = subject.referencePhoto;
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!path) { setUrl(null); return undefined; }
    resolvePhotoUrl(path)
      .then((u) => { if (alive) setUrl(u); })
      .catch(() => { if (alive) setUrl(null); });
    return () => { alive = false; };
  }, [path]);

  const onPick = async (e) => {
    if (!canWrite) return;
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const storagePath = await uploadReferencePhoto(jobId, subject.id, file);
      await handheld.setReferencePhoto({ jobId, subjectId: subject.id, storagePath });
      // onSnapshot updates subject.referencePhoto → the effect resolves the URL.
    } catch (e2) {
      setErr(e2?.message ?? "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const input = <input type="file" accept="image/*" hidden onChange={onPick} />;

  // View only: the photo if there is one, nothing to pick.
  if (!canWrite) {
    return url
      ? <div className="hh-thumb hh-thumb-img" style={{ backgroundImage: `url("${url}")` }} role="img" aria-label="Reference photo" />
      : <div className="hh-thumb hh-thumb-add" aria-hidden="true"><span>no<br />photo</span></div>;
  }

  if (url && !busy) {
    return (
      <label className="hh-thumb hh-thumb-img" style={{ backgroundImage: `url("${url}")` }} title="Replace reference photo">
        {input}
      </label>
    );
  }
  return (
    <label className={`hh-thumb hh-thumb-add ${err ? "bad" : ""}`} title={err || "Add a reference photo"}>
      <span>{busy ? "…" : err ? "retry" : <>+ add<br />photo</>}</span>
      {input}
    </label>
  );
}

// Named exports for tests
export { JobHome, SubjectCard };
